import React, { useEffect, useState } from 'react';

const pad = (n) => String(n).padStart(2, '0');

/** Ticks once a minute while the class is more than an hour away, every second in the last hour. */
export function useClassCountdown(iso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!iso) return undefined;
    let t;
    const target = new Date(iso).getTime();
    const schedule = () => {
      const left = target - Date.now();
      t = setTimeout(() => { setNow(Date.now()); schedule(); }, left > 3_600_000 ? 60_000 : 1000);
    };
    schedule();
    return () => clearTimeout(t);
  }, [iso]);
  if (!iso) return null;
  const left = Math.max(0, new Date(iso).getTime() - now);
  const d = Math.floor(left / 86_400_000);
  const h = Math.floor((left % 86_400_000) / 3_600_000);
  const m = Math.floor((left % 3_600_000) / 60_000);
  const s = Math.floor((left % 60_000) / 1000);
  const short = left <= 0 ? 'now' : d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${pad(m)}m` : `${m}m ${pad(s)}s`;
  return { left, d, h, m, s, expired: left <= 0, short, lastHour: left <= 3_600_000 };
}

/** Big segmented countdown (days hidden once they hit zero; seconds only inside the last hour). */
export default function Countdown({ iso, dark = true, testid = 'ns-countdown' }) {
  const c = useClassCountdown(iso);
  if (!c) return null;
  const segs = [...(c.d > 0 ? [[c.d, 'days']] : []), [c.h, 'hrs'], [c.m, 'min'], ...(c.lastHour ? [[c.s, 'sec']] : [])];
  const box = dark ? 'bg-white/[0.07] border-white/10 text-white' : 'bg-slate-50 border-slate-200 text-slate-900';
  const lab = dark ? 'text-slate-400' : 'text-slate-500';
  return (
    <div className="flex items-end gap-1.5 sm:gap-2" data-testid={testid} data-left={c.left}>
      {segs.map(([v, l], i) => (
        <React.Fragment key={l}>
          {i > 0 && <span className={`pb-4 sm:pb-5 text-base sm:text-lg font-bold ${lab}`}>:</span>}
          <div className={`rounded-xl border px-2.5 py-1.5 sm:px-3 sm:py-2 min-w-[48px] sm:min-w-[58px] text-center ${box}`}>
            <div className="font-[Manrope] text-xl sm:text-2xl md:text-3xl font-extrabold tabular-nums leading-none">{pad(v)}</div>
            <div className={`text-[9px] sm:text-[10px] uppercase tracking-[0.14em] mt-1 ${lab}`}>{l}</div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}
